import { useState } from 'react';
import { Typography, Box, Grid, CircularProgress } from '@mui/material';
import {
  useCreateOpportunityInterest,
  useDeleteOpportunityInterest,
} from '@/lib/api/opportunity-interests.ts';

import { InvestorOpportunitySummaryDTO, OpportunityFilters, PsiService } from '@inversor-hub/types';
import { BaseOpportunityCard, InterestOpportunityCard } from './OpportunityCard';
import { enqueueSnackbar } from 'notistack';
import { StackedSnackbar } from '@/components/ui/StackedSnackbar.tsx';
import SuitabilityTestModal from '@/features/shared/SuitabilityTestModal.tsx';
import OpportunityFiltersComponent from './OpportunityFilters';
import { useAnalytics } from '@/hooks/useAnalytics.ts';
import { ANALYTICS_PLATFORM, trackProductEvent } from '@/lib/analytics/product-events';

interface OpportunitiesGridProps {
  opportunities: InvestorOpportunitySummaryDTO[];
  onAddInterest: (id: string, selectedServices: PsiService[]) => void;
  onDeleteInterest: (id: string) => void;
  showStatus?: boolean;
}

const OpportunitiesGrid = ({
  opportunities,
  onAddInterest,
  onDeleteInterest,
  showStatus,
}: OpportunitiesGridProps) => {
  if (!opportunities || opportunities.length === 0) {
    return (
      <Typography variant="h6" align="center" style={{ marginTop: 20 }}>
        {showStatus
          ? 'No has mostrado interés en ninguna oportunidad todavía, hazlo en la sección "Explorar"'
          : 'No hay oportunidades'}
      </Typography>
    );
  }

  return (
    <Grid container spacing={{ xs: 1.5, sm: 2, md: 3 }}>
      {opportunities.map((opportunity) => (
        <Grid item xs={12} sm={6} md={4} lg={4} key={opportunity.id}>
          {showStatus ? (
            <InterestOpportunityCard
              opportunity={opportunity}
              onDeleteInterest={onDeleteInterest}
            />
          ) : (
            <BaseOpportunityCard opportunity={opportunity} onAddInterest={onAddInterest} />
          )}
        </Grid>
      ))}
    </Grid>
  );
};

interface InvestorOpportunitiesListProps {
  opportunities?: InvestorOpportunitySummaryDTO[];
  isLoading: boolean;
  showOnlyInterests?: boolean;
  hasPassedSuitabilityTest: boolean;
  filters: OpportunityFilters;
  onFilterChange: (filters: OpportunityFilters) => void;
}

const InvestorOpportunitiesList = ({
  opportunities,
  isLoading,
  showOnlyInterests = false,
  hasPassedSuitabilityTest,
  filters,
  onFilterChange,
}: InvestorOpportunitiesListProps) => {
  const { mutateAsync: createOpportunityInterest } = useCreateOpportunityInterest();
  const { mutateAsync: deleteOpportunityInterest } = useDeleteOpportunityInterest();
  const { trackEvent } = useAnalytics();

  const [suitabilityModalOpen, setSuitabilityModalOpen] = useState(false);

  const handleAddInterest = async (opportunityId: string, selectedServices: PsiService[]) => {
    if (!hasPassedSuitabilityTest) {
      setSuitabilityModalOpen(true);
      return;
    }
    await createOpportunityInterest({ opportunityId, selectedServices });
    trackProductEvent(trackEvent, 'interest_sent', {
      opportunity_id: opportunityId,
      entry_point: 'opportunities_list',
      selected_services_count: selectedServices.length,
      role: 'investor',
      platform: ANALYTICS_PLATFORM,
    });
    enqueueSnackbar(
      <StackedSnackbar
        title="Interés añadido"
        message="Encuentra más información en la sección 'Mis intereses'."
      />,
      { variant: 'success' }
    );
  };

  const handleDeleteInterest = async (opportunityId: string) => {
    await deleteOpportunityInterest(opportunityId);
    enqueueSnackbar(
      <StackedSnackbar
        title="Interés eliminado"
        message="Has hecho saber al inversor que ya no estás interesado en esta oportunidad."
      />,
      { variant: 'success' }
    );
    return;
  };

  return (
    <Box sx={{ flexGrow: 1, p: 2 }}>
      {!showOnlyInterests && (
        <OpportunityFiltersComponent filters={filters} onFilterChange={onFilterChange} />
      )}
      {isLoading ? (
        <Box display="flex" justifyContent="center" p={3}>
          <CircularProgress />
        </Box>
      ) : (
        <OpportunitiesGrid
          opportunities={opportunities ?? []}
          onAddInterest={handleAddInterest}
          onDeleteInterest={handleDeleteInterest}
          showStatus={showOnlyInterests}
        />
      )}
      <SuitabilityTestModal
        open={suitabilityModalOpen}
        onClose={() => setSuitabilityModalOpen(false)}
      />
    </Box>
  );
};

export default InvestorOpportunitiesList;
